import { useState, useEffect, useCallback } from "react";
import {
  Loader2, Play, Save, Plus, Trash2, ChevronDown, ChevronRight,
  CheckCircle2, XCircle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD"];
const TIMEZONES = [
  "Europe/Paris", "Europe/London", "Europe/Brussels", "Europe/Madrid",
  "America/New_York", "America/Los_Angeles", "UTC",
];
const INTERVAL_CHOICES = [1, 5, 10, 15, 30, 60, 120, 360, 720, 1440];

const EMPTY = {
  title: "", url: "", method: "GET", enabled: true, save_responses: false,
  schedule_type: "interval", interval_minutes: 15, daily_time: "00:00",
  cron_expression: "*/15 * * * *", timezone: "Europe/Paris",
  timeout: 30, body: "", auth_user: "", auth_password: "", redirect_success: false,
};

function labelInterval(min) {
  if (min < 60) return `${min} minute${min > 1 ? "s" : ""}`;
  if (min === 60) return "1 heure";
  if (min < 1440) return `${min / 60} heures`;
  return `${min / 1440} jour${min > 1440 ? "s" : ""}`;
}

function Radio({ checked, onChange, children }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer group py-1">
      <span
        onClick={onChange}
        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
          checked ? "border-amber-500" : "border-zinc-600 group-hover:border-zinc-500"
        }`}
      >
        {checked && <span className="w-2 h-2 rounded-full bg-amber-500" />}
      </span>
      <span className="flex flex-wrap items-center gap-2 text-sm text-zinc-300">{children}</span>
    </label>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-zinc-500 text-[10px] font-mono uppercase block mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function CronJobModal({
  isOpen, onOpenChange, job, onCreate, onUpdate, onTest, onPreview,
}) {
  const isEdit = Boolean(job);
  const isSystem = job?.type === "system";
  const [form, setForm] = useState(EMPTY);
  const [headers, setHeaders] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [nextRuns, setNextRuns] = useState([]);
  const [scheduleError, setScheduleError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Charge les valeurs du job à l'ouverture
  useEffect(() => {
    if (!isOpen) return;
    setTestResult(null);
    setShowAdvanced(false);
    if (job) {
      setForm({ ...EMPTY, ...job, title: job.type === "http" ? job.title || job.name : job.name });
      setHeaders(Object.entries(job.headers || {}).map(([k, v]) => ({ key: k, value: String(v) })));
    } else {
      setForm(EMPTY);
      setHeaders([]);
    }
  }, [isOpen, job]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Aperçu des prochaines exécutions (déclenché quand le planning change)
  const refreshPreview = useCallback(async () => {
    const schedule = {
      schedule_type: form.schedule_type,
      interval_minutes: Number(form.interval_minutes) || 15,
      daily_time: form.daily_time,
      cron_expression: form.cron_expression,
      timezone: form.timezone,
    };
    const res = await onPreview(schedule);
    if (res && res.error) {
      setScheduleError(res.error);
      setNextRuns([]);
    } else {
      setScheduleError(null);
      setNextRuns(res || []);
    }
  }, [form.schedule_type, form.interval_minutes, form.daily_time, form.cron_expression, form.timezone, onPreview]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(refreshPreview, 350); // laisse le temps de taper
    return () => clearTimeout(t);
  }, [isOpen, refreshPreview]);

  const buildPayload = () => {
    const headerObj = {};
    headers.forEach((h) => {
      if (h.key.trim()) headerObj[h.key.trim()] = h.value;
    });
    const payload = {
      enabled: form.enabled,
      schedule_type: form.schedule_type,
      interval_minutes: Number(form.interval_minutes) || 15,
      daily_time: form.daily_time,
      cron_expression: form.cron_expression,
      timezone: form.timezone,
    };
    if (!isSystem) {
      Object.assign(payload, {
        title: form.title,
        url: form.url,
        method: form.method,
        headers: headerObj,
        body: form.body,
        timeout: Number(form.timeout) || 30,
        auth_user: form.auth_user,
        auth_password: form.auth_password,
        save_responses: form.save_responses,
        redirect_success: form.redirect_success,
      });
    }
    if (isSystem && job?.supports_retention) {
      payload.retention_days = Number(form.retention_days) || 30;
    }
    return payload;
  };

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    setTestResult(await onTest(buildPayload()));
    setIsTesting(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const payload = buildPayload();
    const ok = isEdit ? await onUpdate(job.job_id, payload) : await onCreate(payload);
    setIsSaving(false);
    if (ok !== false) onOpenChange(false);
  };

  const title = isEdit ? (isSystem ? "Configurer la tâche" : "Modifier la tâche") : "Nouvelle tâche";

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border border-zinc-800 max-w-[calc(100vw-1rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono text-amber-400 text-base pr-8">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Identité */}
          {isSystem ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
              <p className="text-white text-sm font-medium">{job.name}</p>
              <p className="text-zinc-500 text-xs mt-1">{job.description}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Titre">
                <Input
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="Keep-alive backend"
                  className="bg-zinc-900 border-zinc-700 text-white text-sm"
                />
              </Field>
              <Field label="URL *">
                <Input
                  value={form.url}
                  onChange={(e) => set("url", e.target.value)}
                  placeholder="https://exemple.com/api/ping"
                  className="bg-zinc-900 border-zinc-700 text-white text-sm font-mono"
                />
              </Field>
            </div>
          )}

          {/* Activation */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-zinc-300">Activer la tâche</span>
              <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} />
            </div>
            {!isSystem && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-zinc-300">Sauvegarder les réponses dans l'historique</span>
                <Switch checked={form.save_responses} onCheckedChange={(v) => set("save_responses", v)} />
              </div>
            )}
            {isSystem && job?.supports_retention && (
              <Field label="Rétention (jours)">
                <Input
                  type="number" min={1} max={3650}
                  value={form.retention_days ?? 30}
                  onChange={(e) => set("retention_days", e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white text-sm font-mono w-28"
                />
              </Field>
            )}
          </div>

          {/* Calendrier d'exécution */}
          <div className="border border-zinc-800 rounded-lg p-3.5">
            <p className="text-zinc-400 text-xs font-mono uppercase mb-2.5">Calendrier d'exécution</p>

            <Radio checked={form.schedule_type === "interval"} onChange={() => set("schedule_type", "interval")}>
              Chaque
              <select
                value={form.interval_minutes}
                onChange={(e) => { set("interval_minutes", Number(e.target.value)); set("schedule_type", "interval"); }}
                className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-white font-mono"
              >
                {INTERVAL_CHOICES.map((m) => (
                  <option key={m} value={m}>{labelInterval(m)}</option>
                ))}
              </select>
            </Radio>

            <Radio checked={form.schedule_type === "daily"} onChange={() => set("schedule_type", "daily")}>
              Tous les jours à
              <input
                type="time"
                value={form.daily_time}
                onChange={(e) => { set("daily_time", e.target.value); set("schedule_type", "daily"); }}
                className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-white font-mono"
              />
            </Radio>

            <Radio checked={form.schedule_type === "cron"} onChange={() => set("schedule_type", "cron")}>
              Personnalisé (crontab)
            </Radio>
            {form.schedule_type === "cron" && (
              <div className="mt-2 ml-6">
                <Input
                  value={form.cron_expression}
                  onChange={(e) => set("cron_expression", e.target.value)}
                  placeholder="*/15 * * * *"
                  className="bg-zinc-900 border-zinc-700 text-white text-sm font-mono"
                />
                <p className="text-zinc-600 text-[10px] font-mono mt-1">
                  min heure jour mois jour-semaine
                </p>
              </div>
            )}

            {/* Aperçu */}
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <p className="text-zinc-500 text-[10px] font-mono uppercase mb-1.5">Prochaines exécutions</p>
              {scheduleError ? (
                <p className="text-red-400 text-xs">{scheduleError}</p>
              ) : nextRuns.length === 0 ? (
                <p className="text-zinc-600 text-xs">—</p>
              ) : (
                <div className="space-y-0.5">
                  {nextRuns.map((r) => (
                    <p key={r} className="text-zinc-400 font-mono text-[11px]">
                      {new Date(r).toLocaleString("fr-FR", {
                        weekday: "long", day: "2-digit", month: "long",
                        year: "numeric", hour: "2-digit", minute: "2-digit",
                      })}
                    </p>
                  ))}
                  <p className="text-zinc-600 text-[10px] pt-1">Fuseau : {form.timezone}</p>
                </div>
              )}
            </div>
          </div>

          {/* Avancé */}
          {!isSystem && (
            <div className="border border-zinc-800 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowAdvanced((v) => !v)}
                className="w-full px-3.5 py-2.5 flex items-center justify-between text-zinc-400 hover:text-zinc-200 transition-colors"
              >
                <span className="text-xs font-mono uppercase">Avancé</span>
                {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {showAdvanced && (
                <div className="px-3.5 pb-3.5 space-y-3 border-t border-zinc-800 pt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Méthode">
                      <select
                        value={form.method}
                        onChange={(e) => set("method", e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-white font-mono"
                      >
                        {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </Field>
                    <Field label="Fuseau horaire">
                      <select
                        value={form.timezone}
                        onChange={(e) => set("timezone", e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-white font-mono"
                      >
                        {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                      </select>
                    </Field>
                  </div>

                  <Field label="En-têtes">
                    <div className="space-y-2">
                      {headers.map((h, i) => (
                        <div key={i} className="flex gap-2">
                          <Input
                            value={h.key}
                            onChange={(e) => setHeaders((hs) => hs.map((x, j) => j === i ? { ...x, key: e.target.value } : x))}
                            placeholder="Nom"
                            className="bg-zinc-900 border-zinc-700 text-white text-xs font-mono h-8"
                          />
                          <Input
                            value={h.value}
                            onChange={(e) => setHeaders((hs) => hs.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                            placeholder="Valeur"
                            className="bg-zinc-900 border-zinc-700 text-white text-xs font-mono h-8"
                          />
                          <button
                            onClick={() => setHeaders((hs) => hs.filter((_, j) => j !== i))}
                            className="text-zinc-600 hover:text-red-400 shrink-0 px-1 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      <Button
                        onClick={() => setHeaders((hs) => [...hs, { key: "", value: "" }])}
                        variant="outline" size="sm"
                        className="border-zinc-700 text-zinc-400 hover:text-amber-400 text-xs h-7"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Ajouter
                      </Button>
                    </div>
                  </Field>

                  {form.method !== "GET" && form.method !== "HEAD" && (
                    <Field label="Corps de la requête">
                      <textarea
                        value={form.body}
                        onChange={(e) => set("body", e.target.value)}
                        rows={3}
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-md text-white text-xs font-mono p-2 resize-none focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                      />
                    </Field>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Délai d'attente (s)">
                      <Input
                        type="number" min={1} max={120}
                        value={form.timeout}
                        onChange={(e) => set("timeout", e.target.value)}
                        className="bg-zinc-900 border-zinc-700 text-white text-sm font-mono"
                      />
                    </Field>
                    <Field label="Auth HTTP (utilisateur)">
                      <Input
                        value={form.auth_user}
                        onChange={(e) => set("auth_user", e.target.value)}
                        placeholder="optionnel"
                        className="bg-zinc-900 border-zinc-700 text-white text-sm font-mono"
                      />
                    </Field>
                  </div>
                  {form.auth_user && (
                    <Field label="Auth HTTP (mot de passe)">
                      <Input
                        type="password"
                        value={form.auth_password}
                        onChange={(e) => set("auth_password", e.target.value)}
                        className="bg-zinc-900 border-zinc-700 text-white text-sm font-mono"
                      />
                    </Field>
                  )}

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-zinc-400">Traiter les redirections 3xx comme un succès</span>
                    <Switch checked={form.redirect_success} onCheckedChange={(v) => set("redirect_success", v)} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Résultat du test */}
          {testResult && (
            <div className={`rounded-lg border p-3 ${
              testResult.status === "success"
                ? "border-emerald-500/30 bg-emerald-500/5"
                : "border-red-500/30 bg-red-500/5"
            }`}>
              <div className="flex items-center gap-2">
                {testResult.status === "success"
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  : <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
                <span className={`text-sm font-mono ${testResult.status === "success" ? "text-emerald-400" : "text-red-400"}`}>
                  {testResult.message}
                </span>
                {testResult.duration_ms != null && (
                  <span className="text-zinc-500 font-mono text-[10px] ml-auto shrink-0">{testResult.duration_ms} ms</span>
                )}
              </div>
              {testResult.response && (
                <pre className="mt-2 text-zinc-500 text-[10px] font-mono whitespace-pre-wrap break-all max-h-24 overflow-y-auto">
                  {testResult.response.slice(0, 500)}
                </pre>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            {!isSystem && (
              <Button
                onClick={handleTest}
                disabled={isTesting || !form.url}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:text-amber-400 hover:border-amber-500/30 font-mono text-xs"
              >
                {isTesting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                Tester
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={isSaving || (!isSystem && !form.url) || Boolean(scheduleError)}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-mono text-xs"
            >
              {isSaving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              {isEdit ? "Enregistrer" : "Créer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
